import React, { useState } from 'react';
import { TrendingUp, Users, ThumbsUp, FileText, Award, ChevronDown, ChevronUp } from 'lucide-react';
import { DualAxisBarChart } from '../../../components/common/DualAxisBarChart';
import { StandardBarChart } from '../../../components/common/StandardBarChart';

export const PR = ({ data, loading, error }) => {
    const [isTableCollapsed, setIsTableCollapsed] = useState(false);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800 text-sm">{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
        <p className="text-gray-600">No PR data available</p>
      </div>
    );
  }

  const { summary, monthly_data, top_placements } = data;

  const MetricCard = ({ icon: Icon, label, value, subtext, trend }) => (
    <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className="w-5 h-5 text-blue-600" />
          <span className="text-sm font-medium text-gray-600">{label}</span>
        </div>
        {trend && (
          <span className="text-sm font-medium text-green-600">+{trend}%</span>
        )}
      </div>
      <div className="text-3xl font-bold text-gray-900 mb-1">{value}</div>
      {subtext && <div className="text-sm text-gray-500">{subtext}</div>}
    </div>
  );

  const getDAColor = (da) => {
    if (da >= 80) return 'text-green-600 bg-green-50';
    if (da >= 60) return 'text-blue-600 bg-blue-50';
    if (da >= 40) return 'text-yellow-600 bg-yellow-50';
    return 'text-gray-600 bg-gray-50';
  };

  // Prepare chart data
  const placementsChartData = monthly_data.map(item => ({
    label: item.month,
    value: item.placements,
  }));

  const reachEngagementChartData = monthly_data.map(item => ({
    label: item.month,
    reach: item.reach,
    engagement: item.engagement,
  }));

  return (
    <div className="space-y-6">
      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <MetricCard
          icon={FileText}
          label="Total Placements"
          value={summary.total_placements}
          subtext="Media mentions"
          trend={summary.placement_growth}
        />
        <MetricCard
          icon={Users}
          label="Total Reach"
          value={`${(summary.total_reach / 1000000).toFixed(2)}M`}
          subtext="Estimated impressions"
          trend={summary.reach_growth}
        />
        <MetricCard
          icon={ThumbsUp}
          label="Total Engagement"
          value={`${(summary.total_engagement / 1000).toFixed(1)}K`}
          subtext="Likes, shares, comments"
          trend={summary.engagement_growth}
        />
        <MetricCard
          icon={TrendingUp}
          label="Engagement Rate"
          value={`${summary.avg_engagement_rate}%`}
          subtext="Avg across placements"
        />
        <MetricCard
          icon={Award}
          label="Avg Domain Authority"
          value={summary.avg_domain_authority}
          subtext="Publication quality"
          trend={summary.da_growth}
        />
      </div>

      {/* Monthly Trends Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Placements Chart */}
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <StandardBarChart
            data={placementsChartData}
            config={{
              dataKey: 'value',
              color: '#152451',
              barName: 'Placements',
              valueFormatter: (value) => value.toLocaleString(),
              labelKey: 'label',
            }}
            title="Monthly Placements"
            showLegend={false}
          />
        </div>

        {/* Reach & Engagement Chart (Dual Axis) */}
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <DualAxisBarChart
            data={reachEngagementChartData}
            config={{
              metric1Key: 'reach',
              metric2Key: 'engagement',
              metric1Name: 'Reach',
              metric2Name: 'Engagement',
              metric1Color: '#009391',
              metric2Color: '#f58025',
              metric1Formatter: (value) => {
                if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
                if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
                return value.toLocaleString();
              },
              metric2Formatter: (value) => {
                if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
                return value.toLocaleString();
              },
              labelKey: 'label',
            }}
            title="Reach & Engagement"
            showLegend={true}
          />
        </div>
      </div>

      {/* Top Placements Table - COLLAPSIBLE */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div 
          className="p-6 border-b border-gray-200 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setIsTableCollapsed(!isTableCollapsed)}
        >
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Top Placements</h3>
            <p className="text-sm text-gray-500 mt-1">
              {top_placements.length} media placements
            </p>
          </div>
          <button
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label={isTableCollapsed ? "Expand table" : "Collapse table"}
          >
            {isTableCollapsed ? (
              <ChevronDown className="w-5 h-5 text-gray-600" />
            ) : (
              <ChevronUp className="w-5 h-5 text-gray-600" />
            )}
          </button>
        </div>
        
        {!isTableCollapsed && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Publication
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    DA
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reach
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Engagement
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Eng. Rate
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Link
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {top_placements.map((placement, index) => {
                  const engRate = ((placement.engagement / placement.reach) * 100).toFixed(2);
                  return (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{placement.publication}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600">{placement.date}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getDAColor(placement.domain_authority)}`}>
                          {placement.domain_authority}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="text-sm text-gray-900">{placement.reach.toLocaleString()}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="text-sm text-gray-900">{placement.engagement.toLocaleString()}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="text-sm text-gray-600">{engRate}%</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <a 
                          href={placement.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          View
                        </a>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};